// /app/signed-in#… — where Google sign-in for the iOS app ends (TOM-555, ADR
// 0350; contract §2.6).
//
// The app opens the sign-in in an ASWebAuthenticationSession, which catches this
// URL before it loads and hands its fragment (a one-time code, or an error) to
// the app. This page shows only in a browser that lands here outside the app. It
// can't finish the sign-in, since only the app holds the PKCE verifier the code
// needs, so it says to go back to the app. It reads the fragment once and then
// takes it out of the address bar and the history.
//
// It never shows the address an identity_conflict carries: anyone can write a
// fragment, and this page must not put a stranger's words on our domain.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { appSignInOutcome, type AppSignInOutcome } from '../lib/appSignIn';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const MESSAGE: Record<AppSignInOutcome, string> = {
  signed_in:
    'You signed in with Google for the Neomaris app. Go back to the app to finish. If it’s still waiting, tap Continue with Google in the app again.',
  identity_conflict:
    'There’s already a Neomaris account for this email. Go back to the app and sign in with your email and password.',
  failed: 'Google sign-in didn’t finish. Go back to the app and try again.',
};

export function AppSignedInView({ outcome }: { outcome: AppSignInOutcome }) {
  return (
    <section className="auth-panel">
      <div className="auth-card" data-testid="app-signed-in" data-outcome={outcome}>
        <h2>Return to the Neomaris app</h2>
        <p className="auth-sub">{MESSAGE[outcome]}</p>
        <p className="auth-sub">
          To use Neomaris in this browser, <Link to="/login">sign in on the website</Link>.
        </p>
      </div>
    </section>
  );
}

export function AppSignedIn() {
  useDocumentTitle('Return to the Neomaris app - Neomaris');
  const [outcome] = useState(() =>
    appSignInOutcome(typeof window === 'undefined' ? '' : window.location.hash)
  );
  useEffect(() => {
    // The code is useless without the app's verifier, but it has no place in
    // this browser's history either.
    if (window.location.hash !== '') {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`
      );
    }
  }, []);
  return <AppSignedInView outcome={outcome} />;
}
